/**
 * O registro das áreas do processo (TUT-175 fase 1).
 *
 * Ele saiu de dentro da `ProcessDetailPage` por dois motivos, e o segundo é o
 * que importa. O primeiro é tamanho: a página tem mais de 1800 linhas e a lista
 * de abas é a parte que mais muda. O segundo é que a guarda que protegia as
 * abas — `core-work-adaptive-flow.test.ts` — afirmava `toContain('"Resumo"')`
 * sobre o TEXTO-FONTE da página. Isso passa se a string estiver num comentário
 * e não enxerga aba declarada sem painel, que é justamente o defeito que já
 * deixou três abas do Console invisíveis. Com o registro em módulo próprio, a
 * guarda compara a lista com os painéis de verdade.
 *
 * **O slug É o id.** Eram dois mapas paralelos (`tabs` de rótulos acentuados e
 * `processTabSlugs` de rótulo → slug), e manter os dois em sincronia era
 * trabalho manual sem guarda. Como a URL nunca carregou acento, o id ASCII já
 * era o slug — a duplicação só criava a chance de divergirem. O rótulo, que é a
 * única parte acentuada, passa a ser dado.
 */


export interface ProcessTabDefinition {
  /** Chave ASCII da aba. É também o valor que viaja em `?aba=`. */
  id: string;
  /** O que a pessoa lê na fita de abas. */
  label: string;
}

/**
 * A ordem é a do escritório, não a nossa: ela reproduz a sequência das faixas
 * do Access que o cliente já usa (Quesitos antes de Perícias porque os quesitos
 * são formulados antes de existir perícia; Laudos depois dela porque o laudo é
 * o que vem da vistoria). Mexer nesta ordem é decisão de produto.
 */
export const PROCESS_TABS = [
  { id: "resumo", label: "Resumo" },
  { id: "quesitos", label: "Quesitos" },
  { id: "pericias", label: "Perícias" },
  { id: "laudos", label: "Laudos" },
  { id: "impugnacoes", label: "Impugnações" },
  { id: "subsidios", label: "Subsídios" },
  // TUT-568 — a aba Agendamento no lugar de Prazos (decisão do dono, 30/10): a
  // lista do Agendamento filtrada neste processo. O `?aba=prazos` antigo é alias.
  { id: "agendamento", label: "Agendamento" },
  { id: "documentos", label: "Documentos" },
  { id: "atendimento", label: "Atendimento" },
  { id: "financeiro", label: "Financeiro" },
  // TUT-570 (D4): a história do acesso no Portal do advogado, depois de Financeiro.
  { id: "acesso", label: "Acesso" },
  // TUT-201: o achado (e) do dono — "Histórico completo" não pertence a uma
  // aba chamada Resumo. A aba junta a família inteira: compositor de
  // movimentação, linha do tempo dos fatos e o feed auditado.
  { id: "historico", label: "Histórico" },
] as const satisfies readonly ProcessTabDefinition[];

export type ProcessTab = (typeof PROCESS_TABS)[number]["id"];

export const PROCESS_TAB_IDS = PROCESS_TABS.map(
  (tab) => tab.id,
) as readonly ProcessTab[];

/**
 * Slugs aposentados e para onde eles vão.
 *
 * `processTabFromParam` já caía em `resumo` para qualquer valor desconhecido,
 * então nenhum link antigo quebrava — mas caía em SILÊNCIO, e a URL continuava
 * anunciando uma aba que não existe mais. O alias declarado é testável, diz na
 * cara por que aquele slug sumiu, e é onde a próxima aba removida entra.
 *
 * `atividade` saiu na TUT-175: o feed auditado passou a morar dentro do Resumo.
 * Na TUT-201 ele mudou de novo — para a aba Histórico — e o alias seguiu
 * junto: o link antigo continua abrindo o MESMO conteúdo, onde quer que ele
 * more agora.
 */
export const PROCESS_TAB_ALIASES: Readonly<Record<string, ProcessTab>> = {
  atividade: "historico",
  // TUT-568 — a aba Prazos saiu; o link antigo abre a aba Agendamento.
  prazos: "agendamento",
};

export const DEFAULT_PROCESS_TAB: ProcessTab = "resumo";

function isProcessTab(value: string): value is ProcessTab {
  return PROCESS_TAB_IDS.includes(value as ProcessTab);
}

/** Resolve o `?aba=` para uma área existente, passando pelos aliases. */
export function processTabFromParam(value: string | null): ProcessTab {
  if (!value) return DEFAULT_PROCESS_TAB;
  if (isProcessTab(value)) return value;
  return PROCESS_TAB_ALIASES[value] ?? DEFAULT_PROCESS_TAB;
}

/**
 * Verdadeiro quando o endereço carrega um slug que não existe mais. A página
 * usa isso para REESCREVER o parâmetro — sem isso a URL ficaria mentindo sobre
 * onde a pessoa está.
 */
export function isStaleProcessTabSlug(value: string | null): boolean {
  return Boolean(value) && !isProcessTab(value!);
}

export function processTabLabel(tab: ProcessTab): string {
  return PROCESS_TABS.find((item) => item.id === tab)!.label;
}

export function processTabId(tab: ProcessTab): string {
  return `process-tab-${tab}`;
}

export function processPanelId(tab: ProcessTab): string {
  return `process-panel-${tab}`;
}
